import React, { useState, useEffect } from 'react';
import api from '../../../../services/api';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  Grid,
  Card,
  CardContent,
  TableSortLabel,
  Pagination,
  Stack,
  List,
  ListItem,
  ListItemText,
  Divider,
  Skeleton,
  Checkbox,
  FormControlLabel,
  Tooltip,
  LinearProgress
} from '@mui/material';
import {
  Search as SearchIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Storage as StorageIcon,
  Clear as ClearIcon,
  Replay as ReplayIcon,
  Visibility as VisibilityIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  YouTube as YouTubeIcon,
  Language as LanguageIcon,
  TrendingUp as TrendingUpIcon,
  Schedule as ScheduleIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import PlatformDialog from '../../../molecules/PlatformDialog/PlatformDialog';
import PlatformDialogTitle from '../../../molecules/PlatformDialog/PlatformDialogTitle';

const CacheManagementPanel = () => {
  const theme = useMuiTheme();
  const [cacheItems, setCacheItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterMediaType, setFilterMediaType] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [sortBy, setSortBy] = useState('scrapedAt');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Estados para paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  
  // Estados para ações
  const [selectedItems, setSelectedItems] = useState([]);
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [reprocessDialogOpen, setReprocessDialogOpen] = useState(false);
  const [clearConfig, setClearConfig] = useState({
    mediaType: '',
    dateBefore: '',
    reuseCountMax: '',
    confirm: false
  });
  const [reprocessUrls, setReprocessUrls] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);

  // Buscar estatísticas do cache
  const fetchStats = async () => {
    try {
      const response = await api.get('/cache/stats');
      setStats(response.data.data);
      setError(''); // Limpar erro se sucesso
    } catch (error) {
      console.error('Erro ao buscar estatísticas:', error);
      if (error.response?.status === 403) {
        setError('Acesso negado. Você não tem permissão para acessar esta funcionalidade.');
      } else {
        setError('Erro ao carregar estatísticas');
      }
    }
  };

  // Buscar itens do cache
  const fetchCacheItems = async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        limit: pageSize,
        sortBy,
        sortOrder
      };

      if (searchTerm) params.search = searchTerm;
      if (filterMediaType) params.mediaType = filterMediaType;
      if (filterDateFrom) params.dateFrom = filterDateFrom;
      if (filterDateTo) params.dateTo = filterDateTo;
      if (filterStatus) params.status = filterStatus;

      const response = await api.get('/cache/items', { params });
      
      setCacheItems(response.data.data.items);
      setTotalPages(response.data.data.pagination.totalPages);
      setTotalItems(response.data.data.pagination.totalItems);
      setError(''); // Limpar erro se sucesso
    } catch (error) {
      console.error('Erro ao buscar itens:', error);
      if (error.response?.status === 403) {
        setError('Acesso negado. Você não tem permissão para acessar esta funcionalidade.');
      } else {
        setError('Erro ao carregar itens do cache');
      }
    } finally {
      setLoading(false);
    }
  };

  // Aplicar filtros
  const applyFilters = () => {
    setCurrentPage(1);
    fetchCacheItems();
  };

  // Limpar filtros
  const clearFilters = () => {
    setSearchTerm('');
    setFilterMediaType('');
    setFilterDateFrom('');
    setFilterDateTo('');
    setFilterStatus('');
    setCurrentPage(1);
    fetchCacheItems();
  };

  // Selecionar/deselecionar item
  const toggleItemSelection = (itemId) => {
    setSelectedItems(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  // Selecionar todos
  const selectAll = () => {
    setSelectedItems(cacheItems.map(item => item._id));
  };

  // Deselecionar todos
  const deselectAll = () => {
    setSelectedItems([]);
  };

  // Limpar cache
  const handleClearCache = async () => {
    setActionLoading(true);
    try {
      const response = await api.delete('/cache/clear', { data: clearConfig });
      
      if (response.data.success) {
        setClearDialogOpen(false);
        setClearConfig({ mediaType: '', dateBefore: '', reuseCountMax: '', confirm: false });
        fetchCacheItems();
        fetchStats();
        setError('');
      }
    } catch (error) {
      setError('Erro ao limpar cache');
      console.error('Erro ao limpar cache:', error);
    } finally {
      setActionLoading(false);
    }
  };

  // Reprocessar URLs
  const handleReprocessUrls = async () => {
    setActionLoading(true);
    try {
      const response = await api.post('/cache/reprocess-batch', {
        urls: reprocessUrls,
        force: false
      });
      
      if (response.data.success) {
        setReprocessDialogOpen(false);
        setReprocessUrls([]);
        setSelectedItems([]);
        fetchCacheItems();
        fetchStats();
        
        // Mostrar resultado do reprocessamento
        const { summary } = response.data;
        const { newCachesCreated, success, errors } = summary;
        
        if (newCachesCreated > 0) {
          setError(`✅ Reprocessamento concluído! ${success} sucessos, ${newCachesCreated} novos caches criados com dados atualizados.`);
        } else if (errors > 0) {
          setError(`⚠️ Reprocessamento concluído com ${errors} erros. Verifique os logs para detalhes.`);
        } else {
          setError(`✅ Reprocessamento concluído!`);
        }
      }
    } catch (error) {
      setError('Erro ao reprocessar URLs');
      console.error('Erro ao reprocessar:', error);
    } finally {
      setActionLoading(false);
    }
  };

  // Remover item específico
  const handleDeleteItem = async (itemId) => {
    if (window.confirm('Tem certeza que deseja remover este item do cache?')) {
      try {
        await api.delete(`/cache/items/${itemId}`);
        fetchCacheItems();
        fetchStats();
      } catch (error) {
        setError('Erro ao remover item');
        console.error('Erro ao remover item:', error);
      }
    }
  };

  // Formatar data
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Formatar número
  const formatNumber = (num) => {
    return num ? num.toLocaleString('pt-BR') : '0';
  };

  // Carregar dados iniciais
  useEffect(() => {
    fetchStats();
    fetchCacheItems();
  }, []);

  // Recarregar quando parâmetros mudarem (mas não se houver erro de permissão)
  useEffect(() => {
    if (!error || !error.includes('Acesso negado')) {
      fetchCacheItems();
    }
  }, [currentPage, pageSize, sortBy, sortOrder]);

  if (loading && !stats) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <StorageIcon sx={{ fontSize: 32, color: 'primary.main', mr: 1.5 }} />
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Gerenciamento de Cache
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Gerencie o cache global de conteúdo raspado
          </Typography>
        </Box>
      </Box>

      {/* Estatísticas */}
      {stats && (
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <StorageIcon color="primary" sx={{ mr: 1 }} />
                  <Box>
                    <Typography variant="h6">{formatNumber(stats.general.totalItems)}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total de Itens
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <TrendingUpIcon color="success" sx={{ mr: 1 }} />
                  <Box>
                    <Typography variant="h6">{formatNumber(stats.general.totalReuses)}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total de Reutilizações
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <YouTubeIcon color="error" sx={{ mr: 1 }} />
                  <Box>
                    <Typography variant="h6">
                      {stats.byMediaType.find(t => t._id === 'YouTube')?.count || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Itens YouTube
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <LanguageIcon color="info" sx={{ mr: 1 }} />
                  <Box>
                    <Typography variant="h6">
                      {stats.byMediaType.find(t => t._id === 'Online')?.count || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Itens Online
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filtros */}
      <Card mb={4}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
            <FilterIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Filtros
          </Typography>
          
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Buscar"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
                placeholder="Título, site ou URL..."
              />
            </Grid>
            
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Plataforma</InputLabel>
                <Select
                  value={filterMediaType}
                  onChange={(e) => setFilterMediaType(e.target.value)}
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="Online">Online</MenuItem>
                  <MenuItem value="YouTube">YouTube</MenuItem>
                  <MenuItem value="Twitter">Twitter/X</MenuItem>
                  <MenuItem value="Instagram">Instagram</MenuItem>
                  <MenuItem value="TikTok">TikTok</MenuItem>
                  <MenuItem value="Facebook">Facebook</MenuItem>
                  <MenuItem value="Threads">Threads</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="new">Novos</MenuItem>
                  <MenuItem value="reused">Reutilizados</MenuItem>
                  <MenuItem value="reprocessed">Reprocessados</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Data Inicial"
                type="date"
                value={filterDateFrom}
                onChange={(e) => setFilterDateFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Data Final"
                type="date"
                value={filterDateTo}
                onChange={(e) => setFilterDateTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12} md={3}>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  onClick={applyFilters}
                  startIcon={<SearchIcon />}
                >
                  Aplicar
                </Button>
                <Button
                  variant="outlined"
                  onClick={clearFilters}
                  startIcon={<ClearIcon />}
                >
                  Limpar
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Ações em lote */}
      {selectedItems.length > 0 && (
        <Card mb={4}>
          <CardContent sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="h6">
                {selectedItems.length} item(s) selecionado(s)
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  startIcon={<ReplayIcon />}
                  onClick={() => {
                    const urls = cacheItems
                      .filter(item => selectedItems.includes(item._id))
                      .map(item => item.url);
                    setReprocessUrls(urls);
                    setReprocessDialogOpen(true);
                  }}
                >
                  Reprocessar
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => {
                    // Implementar remoção em lote
                  }}
                >
                  Remover
                </Button>
              </Stack>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Ações gerais */}
      <Card mb={4}>
        <CardContent sx={{ p: 3 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">Ações Gerais</Typography>
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                color="error"
                startIcon={<ClearIcon />}
                onClick={() => setClearDialogOpen(true)}
              >
                Limpar Cache
              </Button>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => {
                  fetchCacheItems();
                  fetchStats();
                }}
              >
                Atualizar
              </Button>
            </Stack>
          </Box>
        </CardContent>
      </Card>

      {/* Tabela de itens */}
      <Card>
        <CardContent sx={{ p: 3 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
            <Typography variant="h6">
              Itens do Cache ({totalItems})
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="outlined"
                onClick={selectAll}
                disabled={selectedItems.length === cacheItems.length}
              >
                Selecionar Todos
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={deselectAll}
                disabled={selectedItems.length === 0}
              >
                Deselecionar
              </Button>
            </Stack>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" sx={{ pl: 3 }}>
                    <Checkbox
                      checked={selectedItems.length === cacheItems.length && cacheItems.length > 0}
                      indeterminate={selectedItems.length > 0 && selectedItems.length < cacheItems.length}
                      onChange={selectedItems.length === cacheItems.length ? deselectAll : selectAll}
                    />
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'title'}
                      direction={sortBy === 'title' ? sortOrder : 'asc'}
                      onClick={() => {
                        setSortBy('title');
                        setSortOrder(sortBy === 'title' && sortOrder === 'asc' ? 'desc' : 'asc');
                      }}
                    >
                      Título
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Site</TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'reuseCount'}
                      direction={sortBy === 'reuseCount' ? sortOrder : 'asc'}
                      onClick={() => {
                        setSortBy('reuseCount');
                        setSortOrder(sortBy === 'reuseCount' && sortOrder === 'asc' ? 'desc' : 'asc');
                      }}
                    >
                      Reutilizações
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'scrapedAt'}
                      direction={sortBy === 'scrapedAt' ? sortOrder : 'asc'}
                      onClick={() => {
                        setSortBy('scrapedAt');
                        setSortOrder(sortBy === 'scrapedAt' && sortOrder === 'asc' ? 'desc' : 'asc');
                      }}
                    >
                      Data
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, index) => (
                    <TableRow key={index}>
                      <TableCell><Skeleton /></TableCell>
                      <TableCell><Skeleton /></TableCell>
                      <TableCell><Skeleton /></TableCell>
                      <TableCell><Skeleton /></TableCell>
                      <TableCell><Skeleton /></TableCell>
                      <TableCell><Skeleton /></TableCell>
                      <TableCell><Skeleton /></TableCell>
                    </TableRow>
                  ))
                ) : cacheItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body2" color="text.secondary">
                        Nenhum item encontrado
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  cacheItems.map((item) => (
                    <TableRow key={item._id}>
                      <TableCell padding="checkbox" sx={{ pl: 3 }}>
                        <Checkbox
                          checked={selectedItems.includes(item._id)}
                          onChange={() => toggleItemSelection(item._id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, maxWidth: 300 }}>
                          <Typography 
                            variant="body2" 
                            noWrap 
                            sx={{ 
                              flex: 1,
                              cursor: 'pointer',
                              color: 'primary.main',
                              textDecoration: 'underline',
                              '&:hover': {
                                color: 'primary.dark',
                                textDecoration: 'underline'
                              }
                            }}
                            onClick={() => window.open(item.url, '_blank')}
                            title={`Clique para abrir: ${item.url}`}
                          >
                            {item.title}
                          </Typography>
                          <OpenInNewIcon 
                            sx={{ 
                              fontSize: 14, 
                              color: 'primary.main',
                              cursor: 'pointer',
                              '&:hover': { color: 'primary.dark' }
                            }}
                            onClick={() => window.open(item.url, '_blank')}
                            title="Abrir em nova aba"
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={item.mediaType}
                          color={item.mediaType === 'YouTube' ? 'error' : 'primary'}
                          size="small"
                          icon={item.mediaType === 'YouTube' ? <YouTubeIcon /> : <LanguageIcon />}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                          {item.siteName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={item.reuseCount}
                          color={item.reuseCount > 0 ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(item.scrapedAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={
                            item.reprocessedAt 
                              ? 'Reprocessado' 
                              : item.reuseCount > 0 
                                ? 'Reutilizado' 
                                : 'Novo'
                          }
                          color={
                            item.reprocessedAt 
                              ? 'warning' 
                              : item.reuseCount > 0 
                                ? 'success' 
                                : 'primary'
                          }
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Remover item">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteItem(item._id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Paginação */}
          {totalPages > 1 && (
            <Box display="flex" justifyContent="center" mt={3}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(event, page) => setCurrentPage(page)}
                color="primary"
              />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Dialog de limpeza de cache */}
      <PlatformDialog open={clearDialogOpen} onClose={() => setClearDialogOpen(false)} maxWidth="sm" fullWidth>
        <PlatformDialogTitle 
          title="Limpeza Estratégica de Cache"
          subtitle="Remova itens obsoletos para liberar espaço e forçar atualizações."
          icon={<ClearIcon />}
          onClose={() => setClearDialogOpen(false)}
        />
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Plataforma de Mídia</InputLabel>
              <Select
                value={clearConfig.mediaType}
                label="Plataforma de Mídia"
                onChange={(e) => setClearConfig(prev => ({ ...prev, mediaType: e.target.value }))}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="">Todas as mídias</MenuItem>
                <MenuItem value="Online">Apenas Portais (Online)</MenuItem>
                <MenuItem value="YouTube">Apenas YouTube</MenuItem>
                <MenuItem value="Twitter">Apenas Twitter/X</MenuItem>
                <MenuItem value="Instagram">Apenas Instagram</MenuItem>
                <MenuItem value="TikTok">Apenas TikTok</MenuItem>
                <MenuItem value="Facebook">Apenas Facebook</MenuItem>
                <MenuItem value="Threads">Apenas Threads</MenuItem>
              </Select>
            </FormControl>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Data de Corte (Anterior a)"
                  size="small"
                  type="date"
                  value={clearConfig.dateBefore}
                  onChange={(e) => setClearConfig(prev => ({ ...prev, dateBefore: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Máximo de Reutilizações"
                  size="small"
                  type="number"
                  value={clearConfig.reuseCountMax}
                  onChange={(e) => setClearConfig(prev => ({ ...prev, reuseCountMax: e.target.value }))}
                  placeholder="Ex: 0"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
            </Grid>
            
            <Alert severity="warning" variant="outlined" sx={{ borderRadius: 2 }}>
              <strong>Atenção:</strong> Esta operação removerá permanentemente os registros do banco de dados de cache. Dados não salvos em artigos serão perdidos.
            </Alert>
            
            <FormControlLabel
              control={
                <Checkbox
                  checked={clearConfig.confirm}
                  onChange={(e) => setClearConfig(prev => ({ ...prev, confirm: e.target.checked }))}
                />
              }
              label={<Typography variant="body2" sx={{ fontWeight: 600 }}>Estou ciente e desejo prosseguir com a limpeza.</Typography>}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setClearDialogOpen(false)} sx={{ fontWeight: 600 }}>Cancelar</Button>
          <Button
            onClick={handleClearCache}
            variant="contained"
            color="error"
            disabled={!clearConfig.confirm || actionLoading}
            startIcon={actionLoading ? <CircularProgress size={20} color="inherit" /> : <ClearIcon />}
            sx={{ fontWeight: 700, borderRadius: 2, px: 3 }}
          >
            {actionLoading ? 'Processando...' : 'Confirmar Limpeza'}
          </Button>
        </DialogActions>
      </PlatformDialog>

      {/* Dialog de reprocessamento */}
      <PlatformDialog open={reprocessDialogOpen} onClose={() => setReprocessDialogOpen(false)} maxWidth="sm" fullWidth>
        <PlatformDialogTitle 
          title="Forçar Reprocessamento"
          subtitle={`${reprocessUrls.length} link(s) selecionado(s)`}
          icon={<ReplayIcon />}
          onClose={() => setReprocessDialogOpen(false)}
        />
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Typography variant="body2" color="text.secondary">
              Os itens abaixo serão invalidados no cache e colocados na fila para novo scraping imediato:
            </Typography>
            
            <Paper variant="outlined" sx={{ maxHeight: 200, overflowY: 'auto', p: 1, borderRadius: 2, bgcolor: 'action.hover' }}>
              <List dense>
                {reprocessUrls.map((url, index) => (
                  <ListItem key={index}>
                    <ListItemText
                      primary={url}
                      primaryTypographyProps={{ variant: 'caption', noWrap: true, sx: { color: 'primary.main', fontWeight: 600 } }}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
            
            <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
              O reprocessamento garante que os dados mais recentes (engajamento, views) sejam capturados, ignorando versões salvas anteriormente.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setReprocessDialogOpen(false)} sx={{ fontWeight: 600 }}>Cancelar</Button>
          <Button
            onClick={handleReprocessUrls}
            variant="contained"
            disabled={actionLoading}
            startIcon={actionLoading ? <CircularProgress size={20} color="inherit" /> : <ReplayIcon />}
            sx={{ fontWeight: 700, borderRadius: 2, px: 3 }}
          >
            {actionLoading ? 'Iniciando Fila...' : 'Reprocessar Agora'}
          </Button>
        </DialogActions>
      </PlatformDialog>
    </Box>
  );
};

export default CacheManagementPanel;
